/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-unused-prop-types */

import React, { RefObject } from 'react';
import classNames from 'classnames';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';
import { SingleValue } from 'react-select';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { downloadAttachment } from 'src/services/utility';
import { useConnectorTasksQuery } from 'src/services/connector';
import { UseFiltersInterface } from '../../../services/filters';
import styles from '../ContactTable/ContactTable.module.scss';
import Icon from '../../ui/Icon/Icon';
import Select, { Option } from '../../ui/Select/Select';
import TablePagination from '../../ui/TablePagination/TablePagination';
import Badge from '../../ui/Badge/Badge';
import { getPaginationOptions, resolveBadgeTypeForStatus } from '../../../utils/general';

export interface ReturnsTableProps {
  containerRef: RefObject<HTMLDivElement>;
  filterInstance?: UseFiltersInterface;
}

const columnHelper = createColumnHelper<any>();
interface ColumnHelperProps {
  t: (key: string) => string;
}

const CalculatedResultsTable: React.FC<ReturnsTableProps> = ({ containerRef }) => {
  const { t } = useTranslation();
  const { id } = useParams();

  function formatDate(input: string): string {
    const date = new Date(input);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }

  function extractIdFromUrl(url: any) {
    if (!url) return 0;

    const match = url.match(/\/attachments\/(\d+)\/?$/);
    return match ? Number(match[1]) : 0;
  }

  function handleDownload(url: string | null) {
    const id = extractIdFromUrl(url);
    if (!id) return;
    downloadAttachment(id);
  }

  const getColumns = ({ t }: ColumnHelperProps) => [
    columnHelper.accessor(row => row.end_timestamp, {
      id: 'end_timestamp',
      size: 200,
      meta: {
        label: t('general.date'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14 text-full">{formatDate(info.getValue())}</span>,
    }),
    columnHelper.accessor(row => row.type, {
      id: 'type',
      size: 150,
      meta: {
        label: t('general.action'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => <span className="typo-text-14">{info.getValue()}</span>,
    }),
    columnHelper.accessor(row => row.progress, {
      id: 'progress',
      size: 150,
      meta: {
        label: t('general.status'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <Badge
          label={`${t('general.progress')}: ${info.getValue()}`}
          expanded
          showDot
          dotFilled
          type={resolveBadgeTypeForStatus('successful')}
        />
      ),
    }),
    columnHelper.accessor(row => row.data_url, {
      id: 'data_url',
      size: 250,
      meta: {
        label: t('general.file'),
      },
      header: ({ column }) => <span className="typo-text-12 text-faded">{column.columnDef.meta?.label}</span>,
      cell: info => (
        <div className="flex items-end gap-[8px]" onClick={() => handleDownload(info.getValue())}>
          <Icon name="download" width={16} height={16} className="text-primary" />
          <span className="text-primary typo-text-12--bold">
            {extractIdFromUrl(info.getValue()) !== 0
              ? `${t('general.file')} ${extractIdFromUrl(info.getValue())}`
              : t('general.no_data')}
          </span>
        </div>
      ),
    }),
  ];

  const rootRef = React.useRef<HTMLDivElement>(null);
  const [rowSelection, setRowSelection] = React.useState({});

  const options = getPaginationOptions();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: parseInt(options[0].value, 10),
  });

  const { data: connectorTasks } = useConnectorTasksQuery(Number(id), {
    page: pagination.pageIndex + 1,
    size: pagination.pageSize,
  });

  const table = useReactTable({
    data: connectorTasks?.items || [],
    columns: getColumns({ t }),
    state: {
      rowSelection,
      pagination,
    },
    enableRowSelection: true,
    enableSorting: true,
    columnResizeMode: 'onChange',
    manualSorting: true,
    enableColumnResizing: true,
    onRowSelectionChange: setRowSelection,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const handlePerPageUpdate = (selectedOption: SingleValue<Option>) => {
    if (selectedOption) {
      setPagination({ pageIndex: 0, pageSize: Number(selectedOption.value) });
      if (containerRef) {
        containerRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const handleTablePaginationClick = (direction: 'previous' | 'next') => {
    const newIndex = direction === 'next' ? pagination.pageIndex + 1 : pagination.pageIndex - 1;
    setPagination({ ...pagination, pageIndex: newIndex });
    if (containerRef) {
      containerRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className={(styles.tableContent, 'w-full relative')}>
      <div ref={rootRef} className={classNames(styles.root)}>
        <div className={styles.tableWrapper}>
          <table className="w-full">
            <thead>
              {table.getHeaderGroups().map(headerGroup => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map(header => (
                    <th
                      {...{
                        key: header.id,
                        colSpan: header.colSpan,
                        style: {
                          minWidth: header.getSize(),
                        },
                      }}>
                      <div className={styles.headerColumn} onClick={header.column.getToggleSortingHandler()}>
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {{
                          asc: <Icon name="accordion-arrow-up" />,
                          desc: <Icon name="accordion-arrow-down" />,
                        }[header.column.getIsSorted() as string] ?? null}
                      </div>
                      <div
                        {...{
                          onDoubleClick: () => header.column.resetSize(),
                          onMouseDown: header.getResizeHandler(),
                          onTouchStart: header.getResizeHandler(),
                          className: `resizer ${table.options.columnResizeDirection} ${
                            header.column.getIsResizing() ? 'isResizing' : ''
                          }`,
                          style: {
                            transform: '',
                          },
                        }}>
                        <div />
                      </div>
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length > 0 ? (
                table.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => (
                      <td
                        key={cell.id}
                        className={`${cell.column.id !== 'actions' ? 'cursor-pointer' : ''}`}
                        onClick={() => {}}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={table.getAllColumns().length}
                    className="text-center w-full py-4 typo-text-14--bold text-faded">
                    <div className="flex justify-center items-center h-20">{t('general.no_data')}</div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="w-full my-20 flex items-center">
          <div className="flex gap-x-10 flex-1 items-center">
            <div>
              <Select onChange={handlePerPageUpdate} options={options} defaultValue={options[0]} />
            </div>
            <div className="typo-text-12 text-faded">{t('general.per_page')}</div>
          </div>
          <div className="ml-auto">
            <TablePagination
              maxPage={connectorTasks?.pages ?? 0}
              onPaginationClick={handleTablePaginationClick}
              currentPage={pagination.pageIndex}
            />
          </div>
        </div>
      </div>
      <div className={classNames(styles.rightShadow)} />
    </div>
  );
};

export default CalculatedResultsTable;
